<template>
  <!-- Batch history -->
  <article class="batch-history" id="batch-history-header">
    <h2 class="h2">Batch history</h2>
    <p>
      Here you can find the history of all the batches that have been
      processed by Triple-A.
    </p>
    <p>
      You can find out more about the batch processing in the
      <a
        href="https://developers.triple-a.io/docs/triplea-api-doc/0d10abc289f23-payout-statuses"
        target="_blank"
        rel="noopener">Official documentation</a>.
    </p>

    <form>
      <label>Batch ID
        <input type="text" name="batchId" placeholder="Enter batch ID" v-model="searchForm.batch_id" />
      </label>
      <label>Batch name
        <input type="text" name="batchName" placeholder="Enter batch name" v-model="searchForm.batch_name" />
      </label>
      <label>Status
        <select name="status" v-model="searchForm.status">
          <option value="">All</option>
          <option v-for="(status, key) in FILTERED_BATCH_STATUS" :key="`${key}_${status.backend_name}`" :value="status.backend_name">{{ status.display }}</option>
        </select>
      </label>
      <button class="btn icon-btn search" @click.prevent="searchBatch()">Search</button>
      <button class="btn icon-btn reset" @click.prevent="handleResetButton()">Reset</button>
    </form>
    <div class="table-wrapper-custom">
      <div>
        <table>
          <thead>
            <tr>
              <th class="sticky-col left">Batch name</th>
              <th>Status</th>
              <th>Status remarks</th>
              <th>Created</th>
              <th>Last updated</th>
              <th>Batch ID</th>
              <th class="sticky-col right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="batch in batchHistory" :key="batch.id">
              <td class="sticky-col left">
                <button class="btn link-style" @click="seeBatchDetails(batch)">{{ batch.name }}</button>
              </td>
              <td>
                <tag>{{ formatStatus(batch.status) }}</tag>
              </td>
              <td>
                {{ getConsiseStatusRemarks(batch.status_remarks, batch.status) }}
                <button class="btn link-style" v-if="batch.status === FULL_BATCH_STATUS.PARTIAL_COMPLETED.backend_name" @click="seeErrorsOfBatch(batch)">See errors</button>
              </td>
              <td>{{ formatDate(batch.created_at, 'DD-MMM-YY, HH:mm') }} </td>
              <td>{{ formatDate(batch.updated_at, 'DD-MMM-YY, HH:mm') }}</td>
              <td>{{ batch.id }}</td>
              <td class="sticky-col right actions">
                <!-- approve -->
                <button
                  class="approve"
                  v-if="canApproveBatch(batch)"
                  @click="seeBatchDetails(batch)">
                  <svg
                    data-v-4c034fe6=""
                    xmlns="http://www.w3.org/2000/svg"
                    width="24px"
                    height="24px"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="icon-svg feather feather-check-square">
                    <polyline
                      data-v-4c034fe6=""
                      points="9 11 12 14 22 4"></polyline>
                    <path
                      data-v-4c034fe6=""
                      d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                  </svg>
                  <span class="tooltip-text">Approve / cancel batch</span>
                </button>
                <!-- lookup -->
                <button v-if="canLookupBatch(batch)" @click="lookupPayoutTable(batch.id)" class="pure-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#fff">
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </svg>
                  <span class="tooltip-text">View created payouts</span>
                </button>
                <button @click="downloadBatchPayouts(batch)" class="pure-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="#ffffff" d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                  </svg>
                  <span class="tooltip-text">Download whole batch CSV</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <Transition name="fade">
          <p
            class="emptyTable"
            v-if="!batchHistory || (batchHistory && batchHistory.length < 1)">
            {{
            isFirstSearchMade
            ? 'No batch matches this search, please try with other parameters'
            : "You haven't submitted any batch yet"
            }}
          </p>
        </Transition>
      </div>
      <ul>
        <li>
          <button class="btn" :disabled="isLoadingMore || !canLoadMoreBatches" @click="loadMoreBatches()"> {{ isLoadingMore ? 'loading...' : 'Load more' }}</button>
        </li>
      </ul>
    </div>

    <dl>
      <div v-for="status in FILTERED_BATCH_STATUS" :key="`legend_${status.backend_name}`">
        <dt>
          <tag>{{ status.display }}</tag>
        </dt>
        <dd>{{ status.definition }}</dd>
      </div>
    </dl>

    <!-- Batch details popup -->
    <vs-popup :active.sync="isDetailsPopupOpen" @closed="isDetailsPopupOpen = false" class="crypto-batch-details-popup" classContent="crypto-batch-details-wrapper" vs-lg="12"
      vs-md="12">
      <header>
        <h2 class="h3">Batch Details</h2>
        <button @click="downloadBatchPayouts(batchDetails)" class="btn icon-btn download">
          Download batch CSV
        </button>
      </header>
      <p>
        Find here the details of the batch you selected. You can download the CSV and <router-link to="/create-crypto-payout/batch">reuse it for future uploads</router-link>.
      </p>
      <p v-if="isLoadingBatchDetails || !batchDetails.id" class="loader">Loading details...</p>
      <dl v-else>
        <div>
          <dt>Batch name</dt>
          <dd>{{ batchDetails.name }}</dd>
        </div>
        <div>
          <dt>Batch ID</dt>
          <dd>{{ batchDetails.id }}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <tag>{{ formatStatus(batchDetails.status) }}</tag>
          </dd>
          <dd class="remarks">{{ getHumanStatusMessage(selectedBatch.status_remarks, batchDetails.status) }}
            <button class="btn link-style" v-if="batchDetails.status === FULL_BATCH_STATUS.PARTIAL_COMPLETED.backend_name" @click="downloadBatchPayouts(batchDetails, FULL_BATCH_STATUS.FAILED.backend_name)">Download failed payouts CSV</button>
          </dd>
        </div>
        <div v-if="selectedBatch.approval_total ">
          <dt>Approvals given / total approval needed</dt>
          <dd>{{ `${selectedBatch.approvers.length} / ${selectedBatch.approval_total}` }}</dd>
        </div>
        <div v-if="selectedBatch.approvers && selectedBatch.approvers.length > 0">
          <dt>Approved by</dt>
          <dd v-for="approver in selectedBatch.approvers" :key="approver.email">
            {{ approver.email }}, on {{ formatDate(approver.approved_at, 'DD-MMM-YY, HH:mm') }}
          </dd>
        </div>
        <div>
          <dt>Notification email</dt>
          <dd>{{ selectedBatch.notification_email }}</dd>
        </div>
        <!-- <div>
          <dt>Total sending amount submitted</dt>
          <dd v-for="amt in batchDetails.total_sending_amount" :key="'total_sending' + amt.currency">
            {{ amt.currency }} {{ formatFiatPrice(amt.amount) }}
          </dd>
        </div> -->
        <div>
          <dt>Total payouts</dt>
          <dd>{{ batchDetails.total_transfers }}</dd>
        </div>
        <div>
          <dt>Created at</dt>
          <dd>{{ formatDate(selectedBatch.created_at, 'DD-MMM-YY, HH:mm') }}</dd>
        </div>
        <div>
          <dt>Last updated at</dt>
          <dd>{{ formatDate(selectedBatch.updated_at, 'DD-MMM-YY, HH:mm') }}</dd>
        </div>
      </dl>
      <div class="action-group" v-if="isCurrentEntityApprover && (batchDetails && isApprovableStatus(batchDetails.status))">
        <button class="btn approve" @click="approveBatch(selectedBatch)" v-if="canApproveBatch(selectedBatch)">Approve batch creation</button>
        <button class="btn cancel" @click="rejectBatch(selectedBatch)" v-if="canRejectBatch(selectedBatch)">Cancel batch</button>
      </div>
      <p class="banner error" v-if="approvalError">{{ approvalError }}</p>
      <p class="banner error" v-else-if="rejectionError">{{ rejectionError }}</p>
    </vs-popup>

    <!-- Batch errors -->
    <vs-popup :active.sync="isErrorPopupOpen" title="Errors" @closed="isErrorPopupOpen = false" vs-lg="6"
      vs-md="6" class="crypto-batch-errors-popup" classContent="crypto-batch-errors-wrapper">
      <section class="intro-wrapper">
        <p>
          We failed to process some of the payouts in your batch.
          <br /> You can download the error CSV, make corrections, and <router-link to="/create-crypto-payout/batch">re-upload the file</router-link> with the affected payouts to retry them.
        </p>
        <div class="action-group">
          <button class="btn icon-btn save" @click="downloadBatchPayouts(selectedBatch, PYT_STATUS.FAILED.backend_name)">Download error CSV</button>
          <!-- <router-link to="/create-crypto-payout/batch" class="retry">Go to batch upload</router-link> -->
        </div>
      </section>
      <p v-if="isLoadingPayoutsInBatch">Loading...</p>
      <div class="table-wrapper-custom" v-else>
        <div>
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th>Error</th>
                <th>Currency</th>
                <th>Amount</th>
                <th>Payee email</th>
                <th>External Reference</th>
                <th>Mobile number</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(txn, index) of selectedBatch.payouts" :key="txn.email + txn.amount + '_' + index">
                <td>
                  <tag>{{ txn.status }}</tag>
                </td>
                <td>{{ getHumanErrorMessage(txn.status_remarks) }}</td>
                <td>{{ txn.withdraw_currency }}</td>
                <td>{{ formatFiatPrice(txn.withdraw_amount) }}</td>
                <td>{{ txn.email }}</td>
                <td>{{ txn.order_id }}</td>
                <td>{{ txn.mobile_number }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul>
          <li><button class="btn" :disabled="isLoadingMore || !canLoadMorePayouts" @click="loadMoreFailedPayouts()"> {{ isLoadingMore ? 'loading...' : 'Load more' }}</button></li>
        </ul>
      </div>
    </vs-popup>
  </article>
</template>
<script>
import Tag from '../atoms/Tag.vue';
import fileDownload from 'js-file-download';
import { CRYPTO_BATCH_STATUS, CRYPTO_BATCH_PAYOUT_STATUS } from '../constants';

export default {
  components: { Tag },
  props: {
    id: {
      type: String,
      default: ''
    },
    status: {
      type: String,
      default: ''
    },
  },

  data() {
    return {
      FULL_BATCH_STATUS: CRYPTO_BATCH_STATUS,
      FILTERED_BATCH_STATUS: Object.values(CRYPTO_BATCH_STATUS).filter((status) => status.backend_name !== 'creating'),
      PYT_STATUS: CRYPTO_BATCH_PAYOUT_STATUS,
      batchHistory: [],
      isLoadingMore: false,
      canLoadMoreBatches: false,
      canLoadMorePayouts: false,
      isErrorPopupOpen: false,
      isDetailsPopupOpen: false,
      isLoadingBatchDetails: false,
      isLoadingPayoutsInBatch: false,
      isFirstSearchMade: false,
      selectedBatch: {
        id: '',
        name: '',
        updated_at: '',
        status: '',
        status_remarks: '',
        created_at: '',
        notification_email: '',
        payouts: [],
        starting_after: '',
      },

      batchDetails: {
        id: '',
        name: '',
        total_sending_amount: [],
        total_fees: '',
        total_transfers: '',
        created_at: '',
        updated_at: '',
      },

      searchForm: {
        batch_id: '',
        batch_name: '',
        status: '',
      },

      approvalError: '',
      rejectionError: '',
    };
  },

  mounted() {
    this.searchForm.batch_id = this.id;
    this.searchForm.status = this.status;
    this.searchBatch();
  },

  methods: {
    searchBatch() {
      this.getRecaptcha().then((token) => {
        this.searchForm.recaptchaToken = token;
        this.searchForm.starting_after = '';
        this.$store.dispatch('getAllBatchesCryptoPayout', this.searchForm).then((res) => {
          this.batchHistory = res.data.data;
          this.canLoadMoreBatches = res.data.has_more;
          this.searchForm.starting_after = res.data.data[res.data.data.length - 1].id;
        });
      });
    },

    seeErrorsOfBatch(batch) {
      this.selectedBatch = batch;
      this.getPayoutsInBatch(batch.id, this.PYT_STATUS.FAILED.backend_name);
      this.isErrorPopupOpen = true;
    },

    seeBatchDetails(batch) {
      this.selectedBatch = batch;
      this.getBatchDetails(batch);
      this.isDetailsPopupOpen = true;
    },

    getPayoutsInBatch(batchId, pytStatus) {
      this.isLoadingPayoutsInBatch = true;
      this.getRecaptcha().then((token) => {
        const payload = {
          batchId: batchId,
          status: pytStatus,
          recaptchaToken: token,
        };
        this.$store.dispatch('getPayoutsInBatch', payload).then((res) => {
          this.selectedBatch.payouts = res.data.data;
          this.isLoadingPayoutsInBatch = false;
          this.canLoadMorePayouts = res.data.has_more;
        }).catch(() => {
          this.isLoadingPayoutsInBatch = false;
        });
      });
    },

    loadMoreFailedPayouts() {
      const payload = {
        batchId: this.selectedBatch.id,
        status: this.PYT_STATUS.FAILED.backend_name,
      };
      if (this.canLoadMorePayouts && this.selectedBatch.payouts && this.selectedBatch.payouts.length > 0) {
        payload['starting_after'] = this.selectedBatch.payouts[this.selectedBatch.payouts.length - 1].id;
      }
      this.isLoadingMore = true;
      this.getRecaptcha().then((token) => {
        payload.recaptchaToken = token;
        this.$store.dispatch('getPayoutsInBatch', payload).then((res) => {
          this.selectedBatch.payouts = this.selectedBatch.payouts.concat(res.data.data);
          this.canLoadMorePayouts = res.data.has_more;
          this.isLoadingMore = false;
        });
      });
    },

    loadMoreBatches() {
      this.isLoadingMore = true;
      this.getRecaptcha().then((token) => {
        this.searchForm.recaptchaToken = token;
        this.searchForm.starting_after = this.batchHistory[this.batchHistory.length - 1].id;
        this.$store.dispatch('getAllBatchesCryptoPayout', this.searchForm).then((res) => {
          this.batchHistory = this.batchHistory.concat(res.data.data);
          this.canLoadMoreBatches = res.data.has_more;
          this.searchForm.starting_after = res.data.data[res.data.data.length - 1].id;
          this.isLoadingMore = false;
        });
      });
    },

    getBatchDetails(batch) {
      this.getRecaptcha().then((token) => {
        this.isLoadingBatchDetails = true;
        const payload = { batchId: batch.id, recaptchaToken: token };
        this.$store.dispatch('getBatchDetails', payload).then((res) => {
          this.batchDetails = res.data;
          this.isLoadingBatchDetails = false;
        }).catch(() => {
          this.isLoadingBatchDetails = false;
        });
      });
    },

    downloadBatchPayouts(batch, pytStatus) {
      const payload = {
        batchId: batch.id,
        status: pytStatus
      };
      const filename = batch.name.replace('.csv', '') + (pytStatus ? `_${pytStatus} payouts` : '') + '.csv';
      this.getRecaptcha().then((token) => {
        payload.recaptchaToken = token;
        this.$store.dispatch('downloadCryptoPayoutsFromBatch', payload).then((res) => {
          fileDownload(res.data, filename);
        });
      });
    },

    formatStatus(status) {
      if (!status) return '';
      // find in CRYPTO_BATCH_PAYOUT_STATUS the status and return the display name
      const statusObj = Object.values(this.FULL_BATCH_STATUS).find((s) => s.backend_name === status);
      return statusObj ? statusObj.display : status;
    },

    getHumanStatusMessage(status_remarks, status) {
      if (!status_remarks && !status) return '';
      if (status_remarks === 'insufficient_funds') {
        return 'Insufficient funds in your account. Please top up your account to retry the payouts.';
      } else if (status_remarks === 'invalid_currency') {
        return 'Invalid currency. Please check the currency and retry the payouts.';
      } else if (status === this.FULL_BATCH_STATUS.CREATED.backend_name || status === this.FULL_BATCH_STATUS.CREATING.backend_name) {
        return 'Creating the payouts.';
      } else if (status === this.FULL_BATCH_STATUS.PARTIAL_COMPLETED.backend_name) {
        return 'Some payouts failed to process. Please download the error CSV to retry the failed payouts.';
      } else if (status === this.FULL_BATCH_STATUS.FAILED.backend_name) {
        return this.FULL_BATCH_STATUS.FAILED.definition + ' Please download the CSV for more details.';
      } else if (status === this.FULL_BATCH_STATUS.COMPLETED.backend_name) {
        return 'All emails have been sent to recipients.';
      } else if (status === this.FULL_BATCH_STATUS.PROCESSING.backend_name) {
        return 'Creating payouts.';
      } else if (status_remarks === 'a processing error') {
        return 'A processing error occurred. Please retry the payouts.';
      } else {
        return status_remarks;
      }
    },

    getConsiseStatusRemarks(status_remarks, status) {
      if (!status_remarks && !status) return '';
      if (status === this.FULL_BATCH_STATUS.PARTIAL_COMPLETED.backend_name) {
        return 'Some payouts failed to process';
      } else if (status === this.FULL_BATCH_STATUS.FAILED.backend_name) {
        return 'No payouts were created';
      } else if (status === this.FULL_BATCH_STATUS.PROCESSING.backend_name) {
        return 'Creating payouts';
      } else if (status === this.FULL_BATCH_STATUS.COMPLETED.backend_name) {
        return 'Notification sent to recipients';
      } else if (status === this.FULL_BATCH_STATUS.CREATED.backend_name || status === this.FULL_BATCH_STATUS.CREATING.backend_name) {
        return 'Creating payouts';
      } else {
        return status_remarks;
      }
    },

    getHumanErrorMessage(error) {
      if (error === 'insufficient_funds') {
        return 'Insufficient funds in your account. Please top up your account to retry the payouts.';
      } else if (error === 'invalid_currency') {
        return 'Invalid currency. Please check the currency and retry the payouts.';
      } else if (error === 'exists') {
        return 'A payout with the same external reference already exists.';
      }
      else {
        return error;
      }
    },

    getRecaptcha() {
      return this.$recaptchaLoaded().then(() => {
        return this.$recaptcha('login').then((token) => {
          return token;
        });
      }).catch(() => {
        this.$vs.notify({
          title: 'Recaptcha Error',
          text: 'Error while getting token. Please refresh the page.',
          color: 'danger',
        });
      });
    },

    resetForm() {
      // following the same structure as the getAllBatchesCryptoPayout() payload
      this.searchForm = {
        batch_id: '',
        batch_name: '',
        status: '',
      };
    },

    handleResetButton() {
      this.resetForm();
      this.searchBatch();
    },

    canLookupBatch(batch) {
      return batch.status === this.FULL_BATCH_STATUS.COMPLETED.backend_name
        || batch.status === this.FULL_BATCH_STATUS.PARTIAL_COMPLETED.backend_name;
    },

    lookupPayoutTable(batchId) {
      this.$emit('lookup-payout-table', batchId);
    },

    canApproveBatch(batch) {
      const isNotCreatedBy = !batch.created_by || batch.created_by != this.$store.getters.currentActiveEntityAdminGetter.id;
      return this.isApprovableStatus(batch.status) && this.isCurrentEntityApprover && isNotCreatedBy && !batch.has_current_user_approved;
    },

    canRejectBatch (batch) {
      return this.isApprovableStatus(batch.status) && this.isCurrentEntityApprover;
    },

    isApprovableStatus(status) {
      return [
        this.FULL_BATCH_STATUS.PENDING_AUTHORIZATION.backend_name,
      ].includes(status);
    },

    approveBatch(batch) {
      this.rejectionError = '';
      this.approvalError = '';
      this.getRecaptcha().then((token) => {
        const payload = {
          batchId: batch.id,
          recaptchaToken: token,
        };
        this.$store.dispatch('approveCryptoBatchPayout', payload).then((res) => {
          this.$vs.notify({
            title: 'Batch approved',
            text: 'The batch has been approved successfully',
            color: 'green',
            icon: 'check_box'
          });
          this.getBatchDetails(res.data);
          this.searchBatch();
          // manual add since not in response object, to avoid another API call
          this.selectedBatch.has_current_user_approved = true;
          this.selectedBatch.approvers.push({
            email: this.$store.getters.currentActiveEntityAdminGetter.email,
            approved_at: new Date().toISOString(),
          });
        }).catch((e) => {
          this.approvalError = `Failed to approve the batch. ${e.response.data.message} (${e.response.status})`;
        });
      });
    },

    rejectBatch(batch) {
      this.rejectionError = '';
      this.approvalError = '';
      this.getRecaptcha().then((token) => {
        const payload = {
          batchId: batch.id,
          recaptchaToken: token,
        };
        this.$store.dispatch('rejectCryptoBatchPayout', payload).then((res) => {
          this.$vs.notify({
            title: 'Batch cancelled',
            text: 'The batch has been cancelled successfully',
            color: '#0C092D',
            icon: 'check_box',
          });
          this.getBatchDetails(res.data);
          this.searchBatch();
        }).catch((e) => {
          this.rejectionError = `Failed to cancel the batch. ${e.response.data.message} (${e.response.status})`;
        });
      });
    },
  },

  watch: {
    batchHistory: {
      handler() {
        this.isFirstSearchMade = true;
      },
      deep: true,
    },

    activeMerchantGetter() {
      this.resetForm();
      this.searchBatch();
    },

    isDetailsPopupOpen(val) {
      this.approvalError = '';
      this.rejectionError = '';
      if (!val) {
        this.batchDetails = {
          id: '',
          name: '',
          total_sending_amount: [],
          total_fees: '',
          total_transfers: '',
          created_at: '',
          updated_at: '',
        };
      }
    },
  },

  computed: {
    activeMerchantGetter() {
      return this.$store.getters.activeMerchantGetter;
    },
    isCurrentEntityApprover() {
      return this.$store.getters.approversGetter
        .map((approver) => approver.entity_admin_id)
        .includes(this.$store.getters.currentActiveEntityAdminGetter.id);
    },
  }
};
</script>
<style lang="scss">
.batch-history {
  form {
    display: flex;
    gap: 12px;
    align-items: flex-end;
    margin-top: $margin-m;

    label {
      display: flex;
      flex-flow: column;
      width: 250px;

      input {
        padding: 11px;
        border: 1px solid $color-selectable;
        border-radius: 5px;
      }

      option {
        text-transform: capitalize;
      }
    }

    button {
      max-height: 40px;
      min-width: 100px;
      background: $color-primary;
      border: none;
      border-radius: 5px;
      color: white;
      cursor: pointer;
      margin-right: 0px;
    }
  }

  .table-wrapper-custom {
    td {
      button:not(.btn) {
        bottom: 0;
        border: none;
        border-radius: 5px;
        background: $color-primary;
        padding: 3px 4px;
        position: relative;

        &:not(:last-child) {
          margin-right: 5px;
        }

        &:hover {
          background: darken($color-primary, 10%);
        }

        &.approve {
          color: white;
          background-color: $color-primary;

          svg {
            position: relative;
            top: 1px;
          }

          &>.tooltip-text {
            left: -90px;
          }

          &:hover {
            background-color: darken($color-primary, 10%);
          }
        }
      }

      &:first-child {
        max-width: 250px;
        white-space: break-spaces;
        overflow-wrap: anywhere;
        min-width: 200px;

        button.link-style {
          color: $color-text;

          &:hover {
            color: $color-primary;
          }
        }
      }
    }
  }

  dl {
    div {
      display: flex;
      margin-bottom: $margin-s;

      dt {
        margin-bottom: 5px;
        margin-right: $margin-s;
      }
    }
  }
}

.crypto-batch-errors-popup {
  text-align: start;

  .vs-popup>header.vs-popup--header {
    display: flex;
  }

  .vs-popup {
    max-width: 1200px;
    height: max-content;
    max-height: 700px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    .crypto-batch-errors-wrapper.vs-popup--content {
      padding: $margin-l;
      width: 100%;
      top: auto;
      right: auto;
      left: auto;
      bottom: auto;

      div.action-group {
        display: flex;
        justify-content: center;
        margin-top: $margin-m;
        margin-bottom: $margin-l;
        gap: $margin-m;

        button.btn,
        a {
          position: relative;
          margin-right: 0;
          padding: 12px;
          background: $color-primary;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 230px;

          &::before {
            position: relative;
            width: 24px;
            height: 24px;
            left: -12px;
            top: calc(50% - 12px);
            transition: 0.5s;
          }

          &:hover {
            background: darken($color-primary, 10%);
          }
        }

        a.retry {
          &::before {
            content: url('~@/assets/icons/reset_white.svg');
            left: -10px;
          }

          &:hover::before {
            transform: rotate(-360deg);
          }
        }
      }

      .intro-wrapper {
        display: flex;
        justify-content: space-between;
        margin-bottom: $margin-l;

        div.action-group {
          flex-flow: column;
          margin-top: 0px;
          margin-bottom: 0px;
        }

        p {
          margin-bottom: 0px;
        }
      }

      .table-wrapper-custom {
        margin-bottom: $margin-m;

        &>div {
          max-height: 500px;
        }
      }
    }

    .action-group {
      display: flex;
      justify-content: center;
      margin-top: $margin-m;

      button.btn {
        width: 120px;
        margin-right: 0;
      }
    }
  }
}

.crypto-batch-details-popup {
  &>div.vs-popup {
    max-width: 500px;
  }

  .crypto-batch-details-wrapper {
    padding: $margin-l;
    width: 100%;
    top: auto;
    right: auto;
    left: auto;
    bottom: auto;

    header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: $margin-m;
    }

    header+p {
      margin-bottom: $margin-m;
      // font-size: 14px;
      font-family: "Montserrat",
        Helvetica,
        Arial,
        sans-serif;
    }

    p.loader {
      min-height: 450px;
      width: 100%;
      text-align: center;
      line-height: 450px;
    }

    dl {
      div {
        display: flex;
        flex-flow: column;
        margin-bottom: $margin-s;

        dt {
          font-weight: bold;
          margin-right: $margin-s;
        }

        dd.remarks {
          margin-top: 3px;
        }
      }
    }

    .action-group {
      display: flex;
      justify-content: center;
      margin-top: $margin-l;

      button.btn {
        width: 50%;
        max-width: 300px;
        height: 48px;
        margin-right: 0;

        &.approve {
          background: $color-success;
          color: white;

          &:hover {
            background: darken($color-success, 10%);
          }
        }

        &.cancel {
          background: $color-error;
          color: white;
          margin-left: 24px;

          &:hover {
            background: darken($color-error, 10%);
          }
        }
      }
    }

    .action-group+.banner {
      margin-top: $margin-m;
      font-weight: normal;
    }
  }
}
</style>
